import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Undo2, Trash2, Download } from "lucide-react";
import UserHistorySheet from "./UserHistorySheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api } from "../lib/api";
import { fmtDate } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

const eur = (n) => `€ ${(Number(n) || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PLAN = { pro: "Pro", founder: "Fondatore", free: "Free", beta: "Beta Tester" };
const PLAN_OPTIONS = [["free", "Starter (Free)"], ["beta:monthly", "Beta Tester"], ["pro:monthly", "Pro Mensile"], ["pro:annual", "Pro Annuale"], ["founder:lifetime", "Fondatore"]];
const planKey = (s) => (s.plan === "free" ? "free" : s.plan === "beta" ? "beta:monthly" : s.plan === "founder" ? "founder:lifetime" : `pro:${s.billing_cycle === "annual" ? "annual" : "monthly"}`);
const STATUS_FILTERS = [["all", "Tutti"], ["active", "Attivo"], ["beta", "Beta"], ["beta_ended", "Beta terminata"], ["suspended", "Sospeso"], ["cancelled", "Cancellato"], ["free", "Starter"]];
const STATUS_LABEL = { active: "Attivo", suspended: "Sospeso", cancelled: "Cancellato", free: "Starter", beta: "Beta Tester", beta_ended: "Beta terminata" };
const STATUS_CLS = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  suspended: "bg-amber-50 text-amber-700 border-amber-200",
  cancelled: "bg-red-50 text-red-600 border-red-200",
  free: "bg-slate-50 text-slate-500 border-slate-200",
  beta: "bg-indigo-50 text-indigo-700 border-indigo-200",
  beta_ended: "bg-violet-50 text-violet-700 border-violet-200",
};
const CLAUSE_HINTS = ["Garanzia 14 giorni (art. 52 Cod. Consumo)", "Rimborso pro-rata mesi non utilizzati", "Disservizio grave", "Gesto commerciale"];

function RefundModal({ sub, onClose, onDone }) {
  const [mode, setMode] = useState("full");
  const [unit, setUnit] = useState("amount");
  const [value, setValue] = useState("");
  const [revoke, setRevoke] = useState(true);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const preview = mode === "full" ? sub.refundable
    : unit === "amount" ? Number(value) || 0 : Math.round(sub.last_payment_amount * (Number(value) || 0)) / 100;

  const submit = async () => {
    setBusy(true);
    try {
      const body = { mode, revoke, reason };
      if (mode === "partial") body[unit === "amount" ? "amount" : "percent"] = Number(value) || 0;
      const { data } = await api.post(`/super-admin/subscribers/${sub.user_id}/refund`, body);
      toast.success(`Rimborso di ${eur(data.refunded)} registrato${revoke ? " · piano revocato" : ""}`);
      if (data.email_sent) toast.success(`Email "Conferma di Rimborso" inviata al cliente`);
      else toast.warning("Rimborso registrato, ma l'email al cliente non è stata inviata");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rimborso non riuscito"); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="refund-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Gestisci Rimborso</DialogTitle>
          <DialogDescription>{sub.email} · Piano {PLAN[sub.plan]} · pagato {eur(sub.last_payment_amount)} · rimborsabile {eur(sub.refundable)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {[["full", "Rimborso Totale (100%)"], ["partial", "Rimborso Parziale"]].map(([k, l]) => (
              <button key={k} type="button" data-testid={`refund-mode-${k}`} onClick={() => setMode(k)}
                className={`rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors ${mode === k ? "border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-600"}`}>{l}</button>
            ))}
          </div>
          {mode === "partial" && (
            <div className="flex gap-2">
              <div className="flex rounded-lg border border-slate-200 overflow-hidden shrink-0">
                {[["amount", "€"], ["percent", "%"]].map(([k, l]) => (
                  <button key={k} type="button" data-testid={`refund-unit-${k}`} onClick={() => setUnit(k)}
                    className={`px-3 text-sm font-bold ${unit === k ? "bg-primary text-white" : "text-slate-500"}`}>{l}</button>
                ))}
              </div>
              <Input data-testid="refund-value-input" type="number" min="0" step="0.01" value={value} onChange={(e) => setValue(e.target.value)}
                placeholder={unit === "amount" ? "Importo da rimborsare" : "Percentuale da rimborsare"} className="font-mono" />
            </div>
          )}
          <p className="text-sm">Importo rimborso: <span data-testid="refund-preview" className="font-mono font-bold text-slate-900">{eur(preview)}</span></p>
          <label className="flex items-start gap-2.5 text-sm cursor-pointer">
            <Checkbox data-testid="refund-revoke-checkbox" checked={revoke} onCheckedChange={(v) => setRevoke(!!v)} className="mt-0.5" />
            Revoca piano Pro/Fondatore e riporta a Free dopo il rimborso
          </label>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Motivazione / Clausola applicata</p>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {CLAUSE_HINTS.map((c) => (
                <button key={c} type="button" onClick={() => setReason(c)} className="text-[11px] px-2 py-1 rounded-full border border-slate-200 text-slate-600 hover:border-primary hover:text-primary">{c}</button>
              ))}
            </div>
            <Textarea data-testid="refund-reason-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Es. Garanzia 14 giorni, richiesta del cliente…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button data-testid="refund-confirm" onClick={submit} disabled={busy || !reason.trim()} className="font-semibold bg-red-600 hover:bg-red-700">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Undo2 style={{ width: 15, height: 15 }} className="mr-1.5" />} Conferma Rimborso
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SubscribersPanel({ onChanged }) {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const confirmDelete = async () => {
    setDelBusy(true);
    try {
      const { data: d } = await api.delete(`/super-admin/users/${deleting.user_id}`);
      toast.success(`Account eliminato${d.subscription?.had_subscription ? " · abbonamento disdetto" : ""}${d.email_sent ? " · email inviata" : ""}`);
      setDeleting(null); load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eliminazione non riuscita"); }
    setDelBusy(false);
  };
  const [filter, setFilter] = useState("all");
  const [historyId, setHistoryId] = useState(null);
  const load = async () => {
    try { const { data: d } = await api.get("/super-admin/subscribers"); setData(d); } catch (e) { toast.error("Errore caricamento abbonati"); }
  };
  const changePlan = async (s, key) => {
    const [plan, cycle] = key.split(":");
    if (!window.confirm(`Cambiare il piano di ${s.email} in "${PLAN_OPTIONS.find((o) => o[0] === key)[1]}"? Nessun pagamento verrà registrato.`)) return;
    try { await api.post(`/super-admin/users/${s.user_id}/plan`, { plan, cycle: cycle || "monthly" }); toast.success("Piano aggiornato"); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cambio piano non riuscito"); }
  };
  const forceReactivate = async (s) => {
    try {
      const { data: d } = await api.post(`/super-admin/users/${s.user_id}/force-reactivate`, { return_token: true });
      toast.success(`Piano riattivato · nuova scadenza ${fmtDate(d.new_expiry)} · gettone sospensione restituito`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Riattivazione non riuscita"); }
  };
  const exportCsv = async () => {
    try {
      const res = await api.get("/super-admin/users/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `myquoteflow-utenti-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Esportazione non riuscita"); }
  };
  useEffect(() => { load(); }, []);
  if (!data) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;
  const rows = filter === "all" ? data.subscribers : data.subscribers.filter((x) => x.subscription_status === filter);

  return (
    <div className="space-y-6 mb-8">
      <div data-testid="subscribers-card" className="bg-card border border-slate-200/80 rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-base font-semibold text-slate-900">Utenti &amp; Abbonamenti <span className="text-slate-400 font-normal">({rows.length})</span></h2>
          <div className="flex items-center gap-1.5 flex-wrap">
            {STATUS_FILTERS.map(([v, l]) => (
              <button key={v} type="button" data-testid={`sub-filter-${v}`} onClick={() => setFilter(v)}
                className={`px-3 py-1 rounded-full text-xs font-semibold border ${filter === v ? "bg-primary text-white border-primary" : "bg-card text-slate-600 border-slate-200"}`}>
                {l} ({v === "all" ? data.subscribers.length : data.subscribers.filter((x) => x.subscription_status === v).length})
              </button>
            ))}
            <Button size="sm" variant="outline" data-testid="export-users-csv" onClick={exportCsv} className="ml-1"><Download style={{ width: 14, height: 14 }} className="mr-1" /> Esporta CSV</Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="text-left px-4 py-2.5">Utente</th><th className="text-left px-4 py-2.5">Piano</th><th className="text-left px-4 py-2.5">Stato</th><th className="text-right px-4 py-2.5">Pagato</th><th className="text-right px-4 py-2.5">Rimborsato</th><th className="px-4 py-2.5" /></tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400">Nessun utente</td></tr>}
              {rows.map((s) => (
                <tr key={s.user_id} data-testid={`subscriber-row-${s.email}`} className="border-t border-slate-100">
                  <td className="px-4 py-3">
                    <button type="button" data-testid={`user-history-btn-${s.email}`} onClick={() => setHistoryId(s.user_id)} className="text-left group">
                      <p className="font-medium text-slate-900 group-hover:text-primary group-hover:underline">{s.name || "—"}</p><p className="text-xs text-slate-500">{s.email}</p>
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <Select value={planKey(s)} onValueChange={(v) => changePlan(s, v)}>
                      <SelectTrigger data-testid={`plan-select-${s.email}`} className="h-8 w-[150px] text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PLAN_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {s.scadenza_abbonamento && <p className="text-[11px] text-slate-400 mt-1">Scad. {fmtDate(s.scadenza_abbonamento)}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" data-testid={`sub-status-${s.email}`} className={STATUS_CLS[s.subscription_status]}>{STATUS_LABEL[s.subscription_status]}</Badge>
                    {s.suspension && <p className="text-[11px] text-slate-400 mt-1">dal {fmtDate(s.suspension.suspended_at)}</p>}
                    {s.cancel_at_period_end && <p data-testid={`sub-cancel-end-${s.email}`} className="text-[11px] text-red-500 mt-1">Rinnovo disdetto · fino al {fmtDate(s.scadenza_abbonamento)}</p>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{eur(s.last_payment_amount)}</td>
                  <td className="px-4 py-3 text-right font-mono text-red-600">{s.refunded_amount ? `− ${eur(s.refunded_amount)}` : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      {s.subscription_status === "suspended" && <Button size="sm" data-testid={`force-reactivate-${s.email}`} onClick={() => forceReactivate(s)} className="font-semibold bg-emerald-600 hover:bg-emerald-700">Forza Riattivazione</Button>}
                      {s.refundable > 0 && <Button size="sm" variant="outline" data-testid={`refund-btn-${s.email}`} onClick={() => setActive(s)}>Gestisci Rimborso</Button>}
                      {!s.is_super_admin && <Button size="sm" variant="outline" data-testid={`delete-user-btn-${s.email}`} onClick={() => setDeleting(s)} className="text-red-600 border-red-200 hover:bg-red-50"><Trash2 style={{ width: 14, height: 14 }} /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div data-testid="payments-history-card" className="bg-card border border-slate-200/80 rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100"><h2 className="text-base font-semibold text-slate-900">Storico Pagamenti &amp; Rimborsi</h2></div>
        <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
          {data.payments.length === 0 && <p className="px-6 py-6 text-sm text-slate-400 text-center">Nessun movimento</p>}
          {data.payments.map((p) => (
            <div key={p.id} data-testid={`payment-entry-${p.type}`} className="px-6 py-3 flex items-center justify-between gap-3 text-sm">
              <div className="min-w-0">
                <p className="font-medium text-slate-800 truncate">{p.email} · {PLAN[p.plan] || p.plan}</p>
                <p className="text-xs text-slate-500">{fmtDate(p.created_at)}{p.type === "refund" ? ` · ${p.mode === "full" ? "Totale" : "Parziale"}${p.revoked ? " · piano revocato" : ""} · ${p.reason}${p.email_sent ? " · email inviata" : p.email_sent === false ? " · email NON inviata" : ""}` : ""}</p>
              </div>
              <span className={`font-mono font-semibold shrink-0 ${p.type === "refund" ? "text-red-600" : "text-emerald-600"}`}>{p.type === "refund" ? "− " : "+ "}{eur(p.amount)}</span>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!deleting} onOpenChange={(v) => { if (!v) setDeleting(null); }}>
        <DialogContent data-testid="delete-user-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Eliminare l'account?</DialogTitle>
            <DialogDescription>
              {deleting?.email} · Piano {PLAN[deleting?.plan] || deleting?.plan}. L'abbonamento attivo verrà disdetto prima dell'eliminazione (nessun addebito futuro), poi verranno cancellati account e tutti i dati. L'utente riceverà l'email "Account eliminato e abbonamento disdetto". Operazione irreversibile.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Annulla</Button>
            <Button data-testid="delete-user-confirm" onClick={confirmDelete} disabled={delBusy} className="bg-red-600 hover:bg-red-700 font-semibold">
              {delBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 style={{ width: 14, height: 14 }} className="mr-1.5" />} Disdici ed elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UserHistorySheet userId={historyId} onClose={() => setHistoryId(null)} />

      {active && <RefundModal sub={active} onClose={() => setActive(null)} onDone={() => { setActive(null); load(); onChanged?.(); }} />}
    </div>
  );
}
